import {
  createRouter,
  createWebHistory,
  type RouteRecordRaw,
} from 'vue-router';
import { useAuthStore } from '@/shared/stores/auth';

declare module 'vue-router' {
  interface RouteMeta {
    public?: boolean;
    guest?: boolean;
    title?: string;
    superAdminOnly?: boolean;
  }
}

const routes: RouteRecordRaw[] = [
  {
    path: '/',
    name: 'home',
    component: () => import('@/app/pages/PublicHomePage.vue'),
    meta: { public: true, title: 'Home' },
  },
  {
    path: '/portal',
    redirect: () => {
      const auth = useAuthStore();
      return auth.isAuthenticated ? { name: 'dashboard' } : { name: 'auth.login' };
    },
  },
  {
    path: '/login',
    name: 'auth.login',
    component: () => import('@/modules/auth/pages/LoginPage.vue'),
    meta: { public: true, guest: true, title: 'Sign in' },
  },
  {
    path: '/dashboard',
    name: 'dashboard',
    component: () => import('@/modules/dashboard/pages/DashboardPage.vue'),
    meta: { title: 'Dashboard' },
  },
  {
    path: '/forms',
    name: 'conversions.index',
    component: () => import('@/modules/conversions/pages/ConversionsListPage.vue'),
    meta: { title: 'Forms' },
  },
  {
    path: '/forms/new',
    redirect: { name: 'conversions.index', query: { create: '1' } },
  },
  {
    path: '/forms/:id',
    name: 'conversions.show',
    component: () => import('@/modules/conversions/pages/ConversionDetailPage.vue'),
    meta: { title: 'Form details' },
  },
  {
    path: '/submissions',
    name: 'submissions.index',
    component: () => import('@/modules/submissions/pages/SubmissionsListPage.vue'),
    meta: { title: 'Submissions' },
  },
  {
    path: '/submissions/:id',
    name: 'submissions.show',
    component: () => import('@/modules/submissions/pages/SubmissionDetailPage.vue'),
    meta: { title: 'Submission details' },
  },
  {
    path: '/templates',
    redirect: { name: 'conversions.index', query: { create: '1' } },
  },
  {
    path: '/google',
    name: 'google.settings',
    component: () => import('@/modules/google/pages/GoogleSettingsPage.vue'),
    meta: { title: 'Google account', superAdminOnly: true },
  },
  {
    path: '/google/callback',
    name: 'google.callback',
    component: () => import('@/modules/google/pages/GoogleCallbackPage.vue'),
    meta: { public: true, title: 'Google connected' },
  },
  {
    path: '/privacy',
    name: 'legal.privacy',
    component: () => import('@/app/pages/PrivacyPolicyPage.vue'),
    meta: { public: true, title: 'Privacy Policy' },
  },
  {
    path: '/terms',
    name: 'legal.terms',
    component: () => import('@/app/pages/TermsOfServicePage.vue'),
    meta: { public: true, title: 'Terms of Service' },
  },
  {
    path: '/:pathMatch(.*)*',
    name: 'not-found',
    component: () => import('@/app/pages/NotFoundPage.vue'),
    meta: { public: true, title: 'Not Found' },
  },
];

const router = createRouter({
  history: createWebHistory(import.meta.env.BASE_URL),
  routes,
  scrollBehavior: () => ({ top: 0 }),
});

let sessionReady = false;
export function markSessionReady(): void {
  sessionReady = true;
}

router.beforeEach(async (to) => {
  const auth = useAuthStore();

  if (!to.meta.public && auth.accessToken && !auth.user && !sessionReady) {
    return true;
  }

  if (to.meta.public && to.meta.guest && auth.isAuthenticated) {
    return { name: 'dashboard' };
  }

  if (!to.meta.public && !auth.isAuthenticated) {
    if (auth.accessToken && !auth.user) return true;
    return { name: 'auth.login', query: { redirect: to.fullPath } };
  }

  if (to.meta.superAdminOnly && !auth.isSuperAdmin) {
    return { name: 'dashboard' };
  }

  if (to.meta.title) {
    document.title = `${to.meta.title} · ${import.meta.env.VITE_APP_NAME ?? "I'm Hungry Forms"}`;
  }

  return true;
});

export default router;
